import { Chip, Stack, Typography } from "@mui/material";
import { grey } from "@mui/material/colors";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import {
  InverterResponse,
  LiveData,
  useGetInverters,
} from "api";
import { Widget } from "components/elements/mui";
import { useFormatMessage } from "lang";
import { INVERTER } from "routes/consts";
import { getSelectedPowerPlant, useAppSelector } from "store";

type Status = LiveData["status"];

const STATUS_COLOR: Record<Status, "success" | "error" | "default"> = {
  producing: "success",
  offline: "error",
  consuming: "default",
  idle: "default",
};

const fmt = (
  value: number | undefined | null,
  unit: string,
  decimals = 2,
) =>
  value === undefined || value === null
    ? "—"
    : `${value.toFixed(decimals)} ${unit}`;

const StatRow = ({ label, value }: { label: string; value: string }) => (
  <Stack direction="row" spacing={2} alignItems="baseline" justifyContent="flex-end">
    <Typography variant="caption" sx={{ color: grey[700] }}>
      {label}
    </Typography>
    <Typography variant="body2" fontWeight={600}>
      {value}
    </Typography>
  </Stack>
);

const InverterCard = ({
  inverter,
  liveDataInterval,
  dataUpdatedAt,
  size,
}: {
  inverter: InverterResponse;
  liveDataInterval: number;
  dataUpdatedAt: number;
  size: { xs: number; sm?: number; md?: number };
}) => {
  const formatMessage = useFormatMessage();
  const navigate = useNavigate();

  // Mirror routes/utils.ts (PV-level offline computation): only re-check
  // staleness when a fetch lands, otherwise a backgrounded tab returns
  // with `Date.now()` far ahead of the cached `live.date` and flashes
  // "offline" before the focus refetch finishes.
  const [stale, setStale] = useState(false);
  const staleThreshold = Math.max(4 * liveDataInterval, 60_000);
  useEffect(() => {
    const ts = inverter.liveData?.date ?? inverter.lastSeenAt;
    setStale(
      !ts || Date.now() - new Date(ts).getTime() > staleThreshold,
    );
  }, [
    dataUpdatedAt,
    inverter.liveData?.date,
    inverter.lastSeenAt,
    staleThreshold,
  ]);

  const status: Status = stale ? "offline" : inverter.liveData?.status ?? "idle";
  // Headline is the panels' DC input power, matching the plant-level
  // CurrentPower widget for meterless plants.
  const power = inverter.liveData?.dcPower;
  const today = inverter.liveData?.energyProducedToday;
  const temp = inverter.liveData?.inverterTemperature;

  return (
    <Widget
      size={size}
      title={formatMessage("pageTitle.inverter")}
      icon="solar_power"
      customOnClick={() => navigate(INVERTER)}
      customOnClickIcon="open_in_full"
      content={
        <Stack spacing={1}>
          <Typography variant="subtitle2" fontWeight={700} noWrap>
            {inverter.name}
          </Typography>
          <Stack
            direction="row"
            spacing={2}
            alignItems="flex-start"
            justifyContent="space-between"
          >
            {/* Left: live DC power. Unit hidden when there's no reading so
                we don't render a dangling "— kW". */}
            <Stack spacing={0} minWidth={0} flexShrink={1}>
              <Typography variant="h5" fontWeight={700} lineHeight={1}>
                {power !== undefined ? power.toFixed(2) : "—"}
              </Typography>
              {power !== undefined && (
                <Typography variant="caption" sx={{ color: grey[700] }}>
                  kW
                </Typography>
              )}
            </Stack>

            {/* Right: status + per-stat readings. */}
            <Stack spacing={1} alignItems="flex-end" flexShrink={0}>
              <Chip
                size="small"
                label={formatMessage(`status.${status}` as const)}
                color={STATUS_COLOR[status]}
                sx={{ height: 20 }}
              />
              <StatRow
                label={formatMessage("inverter.summary.today")}
                value={fmt(today, "kWh", 1)}
              />
              <StatRow
                label={formatMessage("inverter.summary.temperature")}
                value={fmt(temp, "°C", 0)}
              />
            </Stack>
          </Stack>
        </Stack>
      }
    />
  );
};

export const InverterSummary = () => {
  const selectedPowerPlant = useAppSelector(getSelectedPowerPlant);
  const liveDataInterval = selectedPowerPlant?.liveDataInterval ?? 15_000;
  const { data: inverters, dataUpdatedAt } = useGetInverters(liveDataInterval);

  if (!inverters || inverters.length === 0) return null;

  // Tile sizing: solo inverter takes the full row, two side-by-side on
  // desktop, three or more wrap as 4/12.
  const tileSize =
    inverters.length === 1
      ? { xs: 12 }
      : inverters.length === 2
        ? { xs: 12, sm: 6 }
        : { xs: 12, sm: 6, md: 4 };

  return (
    <>
      {inverters.map((inv) => (
        <InverterCard
          key={inv.id}
          inverter={inv}
          liveDataInterval={liveDataInterval}
          dataUpdatedAt={dataUpdatedAt}
          size={tileSize}
        />
      ))}
    </>
  );
};
